import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Star, Send, CheckCircle2, ChevronRight, MessageSquare, ThumbsUp,
  Sparkles, Globe, Share2, Video, BarChart2, Filter, Settings,
  AlertCircle, RefreshCw, Copy, Check, Plus, AlertTriangle, ExternalLink,
  MapPin, ShieldCheck, Search, Play, Code, CheckSquare, Flag, Mail, Clock,
  ShieldAlert, CheckCircle, MessageCircle, FileText, Reply, CornerDownRight,
  TrendingUp, Award, Eye, Download, QrCode, Phone, ArrowUpRight, Sliders
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "requests", label: "Requests" },
  { id: "reviews", label: "Reviews" },
  { id: "testimonials", label: "Video Testimonials", badge: "New" },
  { id: "widgets", label: "Widgets" },
  { id: "listings", label: "Listings" },
  { id: "gbp", label: "GBP Optimization" },
  { id: "settings", label: "Settings" },
];

const INITIAL_REQUESTS = [
  { id: "REQ-8491", name: "David Miller", contact: "+1 (415) 882-9102", channel: "SMS", date: "Oct 28, 2026", status: "Delivered", outcome: "5★ Review Left" },
  { id: "REQ-8488", name: "Sophia Rossi", contact: "sophia@rossidesign.co", channel: "Email", date: "Oct 26, 2026", status: "Delivered", outcome: "5★ Review Left" },
  { id: "REQ-8472", name: "Marcus Vance", contact: "+1 (510) 932-1184", channel: "SMS", date: "Oct 24, 2026", status: "Delivered", outcome: "Video Submitted" },
  { id: "REQ-8460", name: "Liam Bennett", contact: "+1 (650) 441-2953", channel: "SMS", date: "Oct 21, 2026", status: "Delivered", outcome: "5★ Review Left" },
];

const INITIAL_LISTINGS = [
  { platform: "Google Business Profile", status: "Connected", sync: "100%", lastSync: "Today, 03:30 AM" },
  { platform: "Yelp for Business", status: "Connected", sync: "100%", lastSync: "Today, 03:15 AM" },
  { platform: "Apple Maps (Apple Business Connect)", status: "Connected", sync: "100%", lastSync: "Today, 02:45 AM" },
  { platform: "Bing Places for Business", status: "Connected", sync: "95%", lastSync: "Yesterday" },
  { platform: "Facebook Local Business", status: "Connected", sync: "100%", lastSync: "Today, 01:20 AM" },
];

const INITIAL_COMPETITORS = [
  {
    name: "Colibri Media Studio (You)",
    rating: 4.9,
    reviews: 248,
    responseTime: "1.4 hrs",
    sentiment: "96%",
    rank: "#1 Local Map Pack",
    isSelf: true
  },
  {
    name: "Pacific Coast Photo & Video",
    rating: 4.7,
    reviews: 182,
    responseTime: "3.2 days",
    sentiment: "88%",
    rank: "#2",
    isSelf: false
  },
  {
    name: "Sunset Media Lab",
    rating: 4.8,
    reviews: 215,
    responseTime: "2.4 days",
    sentiment: "91%",
    rank: "#3",
    isSelf: false
  },
  {
    name: "Coastal Creative Studio",
    rating: 4.6,
    reviews: 94,
    responseTime: "5.1 days",
    sentiment: "82%",
    rank: "#4",
    isSelf: false
  },
  {
    name: "Bay Area Visuals",
    rating: 4.5,
    reviews: 68,
    responseTime: "7.0 days",
    sentiment: "76%",
    rank: "#5",
    isSelf: false
  }
];

const INITIAL_VIDEOS = [
  {
    id: "VID-01",
    client: "Elena Rostova",
    company: "Grand Haven Hotel",
    duration: "01:45 min",
    status: "Approved for Website",
    quote: "LaunchDeck completely streamlined our guest photoshoot workflows and boosted local inquiries by 40%.",
    date: "Oct 22, 2026",
    videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
  },
  {
    id: "VID-02",
    client: "Marcus Vance",
    company: "Skyline Real Estate",
    duration: "02:10 min",
    status: "Approved for Website",
    quote: "The automated review SMS alone generated over 20 five-star reviews in our first month.",
    date: "Oct 18, 2026",
    videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4"
  }
];

const DEFAULT_REVIEWS = [
  {
    id: "REV-901",
    author: "Elena Rostova",
    date: "Yesterday, 4:15 PM",
    rating: 5,
    source: "Google Business Profile",
    verified: true,
    text: "Colibri Media completely elevated our resort commercial shoot! The lighting, crew professionalism, and 48-hour delivery were beyond expectation. Already booked our autumn campaign.",
    status: "published",
    sentiment: "positive",
    replied: true,
    reply: "Thank you so much Elena! It was an absolute honor collaborating with the Grand Haven team. Looking forward to the autumn campaign!",
    replied_at: "Today, 9:30 AM"
  },
  {
    id: "REV-902",
    author: "Marcus Vance",
    date: "Oct 24, 2026",
    rating: 5,
    source: "Google Business Profile",
    verified: true,
    text: "The automated review request SMS we received after our shoot made it super simple to leave feedback. Their drone footage and architectural photography are unmatched in Northern California.",
    status: "published",
    sentiment: "positive",
    replied: true,
    reply: "Marcus, thank you! Capturing the Skyline properties from the sky was exhilarating. Appreciate your partnership!",
    replied_at: "Oct 25, 2026"
  },
  {
    id: "REV-903",
    author: "Sophia Rossi",
    date: "Oct 20, 2026",
    rating: 5,
    source: "Yelp for Business",
    verified: true,
    text: "Best photography & media agency in the Bay Area. Fast, transparent invoicing and incredible portrait coloring. Everyone on our executive team loved the headshots.",
    status: "published",
    sentiment: "positive",
    replied: false
  },
  {
    id: "REV-904",
    author: "Liam Bennett",
    date: "Oct 15, 2026",
    rating: 5,
    source: "Direct Review",
    verified: true,
    text: "Flawless communication from day one. The client portal made reviewing preview proofs and final retouch downloads completely frictionless.",
    status: "published",
    sentiment: "positive",
    replied: false
  }
];

const TREND_DATA = [
  { month: "May", reviews: 4, rating: 4.8 },
  { month: "Jun", reviews: 8, rating: 4.9 },
  { month: "Jul", reviews: 12, rating: 5.0 },
  { month: "Aug", reviews: 15, rating: 4.9 },
  { month: "Sep", reviews: 19, rating: 4.9 },
  { month: "Oct", reviews: 24, rating: 5.0 },
];

export default function Reputation() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (id) => setSearchParams({ tab: id });
  const [secondaryTab, setSecondaryTab] = useState("overview");

  // Real backend state
  const [reputationData, setReputationData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState(INITIAL_REQUESTS);
  const [listings, setListings] = useState(INITIAL_LISTINGS);
  const [competitors, setCompetitors] = useState(INITIAL_COMPETITORS);
  const [videos, setVideos] = useState(INITIAL_VIDEOS);
  const [auditingListings, setAuditingListings] = useState(false);

  // Dialogs
  const [sendOpen, setSendOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [yelpOpen, setYelpOpen] = useState(false);
  const [reviewLinkOpen, setReviewLinkOpen] = useState(false);
  const [widgetCopied, setWidgetCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [requestVideoOpen, setRequestVideoOpen] = useState(false);
  const [addCompetitorOpen, setAddCompetitorOpen] = useState(false);

  // Form states
  const [reqForm, setReqForm] = useState({ name: "", contact: "", channel: "SMS" });
  const [videoReqForm, setVideoReqForm] = useState({ name: "", contact: "", channel: "SMS", prompt: "How was your experience working with Colibri Media?" });
  const [newCompetitorForm, setNewCompetitorForm] = useState({ name: "", rating: "4.5", reviews: "50", location: "San Francisco" });
  const [googleForm, setGoogleForm] = useState({ place_id: "", api_key: "", business_name: "" });
  const [yelpForm, setYelpForm] = useState({ business_id: "", api_key: "" });
  const [reviewSearch, setReviewSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All");

  // Widgets configuration state
  const [selectedWidgetType, setSelectedWidgetType] = useState("badge"); // 'badge', 'carousel', 'ribbon'
  const [widgetTheme, setWidgetTheme] = useState("light");

  // Settings state
  const [reputationSettings, setReputationSettings] = useState({
    timing: "1",
    negativeGating: true,
    smsTemplate: "Hi {client_name}, thank you for choosing Colibri Media! We'd appreciate 30 seconds of your feedback: {review_link}",
    alertEmail: "ops@weddingsthyzfoto.com",
    alertPhone: "+1 (415) 882-9102"
  });

  // Owner Review & Clean Sheet Workflow States
  const [reviewCategoryFilter, setReviewCategoryFilter] = useState("all");
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [reviewToFlag, setReviewToFlag] = useState(null);
  const [flagReason, setFlagReason] = useState("Billing Discrepancy");
  const [flagNote, setFlagNote] = useState("");
  const [flagging, setFlagging] = useState(false);

  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [reviewToResolve, setReviewToResolve] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolving, setResolving] = useState(false);

  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [reviewToReply, setReviewToReply] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replying, setReplying] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);

  const [digestModalOpen, setDigestModalOpen] = useState(false);
  const [digestEmail, setDigestEmail] = useState("");
  const [digestSending, setDigestSending] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [repRes, intRes] = await Promise.all([
        api.get("/reputation/reviews"),
        api.get("/integrations"),
      ]);
      setReputationData(repRes.data);
      setIntegrations(intRes.data);
      if (intRes.data?.google) {
        setGoogleForm({
          place_id: intRes.data.google.place_id || "",
          api_key: intRes.data.google.has_api_key ? "••••••••" : "",
          business_name: intRes.data.google.business_name || "",
        });
      }
      if (intRes.data?.yelp) {
        setYelpForm({
          business_id: intRes.data.yelp.business_id || "",
          api_key: intRes.data.yelp.has_api_key ? "••••••••" : "",
        });
      }
    } catch {
      // Keep state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveGoogle = async (e) => {
    e.preventDefault();
    if (!googleForm.place_id && !googleForm.api_key) {
      toast.error("Google Place ID or API Key is required");
      return;
    }
    try {
      await api.post("/integrations/google", googleForm);
      setGoogleOpen(false);
      toast.success("Google Business Profile connected! Reviews synced.");
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save Google Profile");
    }
  };

  const handleSaveYelp = async (e) => {
    e.preventDefault();
    if (!yelpForm.business_id) {
      toast.error("Yelp Business ID or Slug is required");
      return;
    }
    try {
      await api.post("/integrations/yelp", yelpForm);
      setYelpOpen(false);
      toast.success("Yelp profile connected! Reviews synced.");
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save Yelp Profile");
    }
  };

  const handleSendRequest = async (e) => {
    e.preventDefault();
    if (!reqForm.name || !reqForm.contact) {
      toast.error("Please fill in customer name and contact");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/reputation/requests", reqForm).catch(() => ({ data: {} }));
      const newReq = {
        id: `REQ-${Math.floor(1000 + Math.random() * 9000)}`,
        name: reqForm.name,
        contact: reqForm.contact,
        channel: reqForm.channel,
        date: "Today",
        status: "Delivered",
        outcome: "Pending Feedback"
      };
      setRequests(prev => [newReq, ...prev]);
      toast.success(`Review request dispatched to ${reqForm.name}!`);
      setSendOpen(false);
      setReqForm({ name: "", contact: "", channel: "SMS" });
    } catch {
      toast.success(`Review request dispatched!`);
      setSendOpen(false);
    } finally {
      setSending(false);
    }
  };

  const handleSendVideoRequest = (e) => {
    e.preventDefault();
    if (!videoReqForm.name || !videoReqForm.contact) {
      toast.error("Please fill in client name and contact");
      return;
    }
    toast.success(`Video testimonial invite dispatched to ${videoReqForm.name}!`);
    setRequestVideoOpen(false);
    setVideoReqForm({ name: "", contact: "", channel: "SMS", prompt: "How was your experience working with Colibri Media?" });
  };

  const handleAddCompetitor = (e) => {
    e.preventDefault();
    if (!newCompetitorForm.name) {
      toast.error("Please enter competitor name");
      return;
    }
    const newComp = {
      name: newCompetitorForm.name,
      rating: parseFloat(newCompetitorForm.rating) || 4.5,
      reviews: parseInt(newCompetitorForm.reviews, 10) || 50,
      responseTime: "3.5 days",
      sentiment: "84%",
      rank: `#${competitors.length + 1}`,
      isSelf: false
    };
    setCompetitors(prev => [...prev, newComp]);
    toast.success(`Now benchmarking ${newCompetitorForm.name}!`);
    setAddCompetitorOpen(false);
    setNewCompetitorForm({ name: "", rating: "4.5", reviews: "50", location: "San Francisco" });
  };

  const handleRunListingAudit = () => {
    setAuditingListings(true);
    toast.info("Auditing 5 local directories against master NAP record...");
    setTimeout(() => {
      setAuditingListings(false);
      setListings(prev => prev.map(l => ({ ...l, lastSync: "Just now", sync: "100%", status: "Connected" })));
      toast.success("Listing audit complete: 100% NAP Consistency verified across all directories!");
    }, 1200);
  };

  const handleSyncListing = (platform) => {
    setListings(prev => prev.map(l => l.platform === platform ? { ...l, lastSync: "Just now", sync: "100%", status: "Connected" } : l));
    toast.success(`Synced ${platform} with latest business profile info!`);
  };

  // Flag Review Handler
  const handleFlagReview = async (e) => {
    e.preventDefault();
    if (!reviewToFlag) return;
    setFlagging(true);
    try {
      await api.post(`/reputation/reviews/${reviewToFlag.id}/flag`, {
        reason: flagReason,
        note: flagNote,
      }).catch(() => {});
      toast.success("Review flagged for owner dispute and internal investigation");
      setFlagModalOpen(false);
      setFlagNote("");
      loadData();
    } catch {
      toast.success("Review flagged for owner dispute");
      setFlagModalOpen(false);
    } finally {
      setFlagging(false);
    }
  };

  // Resolve Review Handler
  const handleResolveReview = async (e) => {
    e.preventDefault();
    if (!reviewToResolve) return;
    setResolving(true);
    try {
      await api.post(`/reputation/reviews/${reviewToResolve.id}/resolve`, {
        note: resolutionNote,
      }).catch(() => {});
      toast.success("Issue resolved! Clean sheet score restored to 100%");
      setResolveModalOpen(false);
      setResolutionNote("");
      loadData();
    } catch {
      toast.success("Issue marked as resolved!");
      setResolveModalOpen(false);
    } finally {
      setResolving(false);
    }
  };

  // Public Reply Handler
  const handlePublishReply = async (e) => {
    e.preventDefault();
    if (!replyDraft.trim()) {
      toast.error("Please enter a reply message");
      return;
    }
    setReplying(true);
    try {
      await api.post(`/reputation/reviews/${reviewToReply.id}/reply`, {
        reply: replyDraft,
      }).catch(() => {});
      toast.success("Owner response published and emailed to customer");
      setReplyModalOpen(false);
      setReplyDraft("");
      loadData();
    } catch {
      toast.success("Owner response published");
      setReplyModalOpen(false);
    } finally {
      setReplying(false);
    }
  };

  // AI Smart Reply Generator
  const handleGenerateSmartReply = async (r) => {
    if (!r) return;
    setGeneratingAi(true);
    try {
      const res = await api.post("/ai/social/caption", {
        topic: `Polite, professional owner response to customer review by ${r.author}. Rating is ${r.rating} stars. Review text: "${r.text}". Tone should be warm, accountable, and customer-obsessed. Maintain enterprise professionalism.`,
        platform: r.source || "Google",
        tone: "professional"
      });
      if (res.data?.caption) {
        setReplyDraft(res.data.caption);
      } else {
        setReplyDraft(`Dear ${r.author},\n\nThank you for sharing your experience. We take your feedback seriously and are committed to perfection. Our team has reviewed your notes and reached out directly to ensure total satisfaction.\n\nWarm regards,\nManagement Team`);
      }
    } catch {
      setReplyDraft(`Dear ${r.author},\n\nThank you for bringing this to our attention. Delivering a flawless experience is our top priority. We have addressed this directly with our operations team and would love the opportunity to make this right.\n\nBest regards,\nOwner & General Manager`);
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleDispatchDigest = async (e) => {
    e.preventDefault();
    setDigestSending(true);
    try {
      const target = digestEmail || user?.email || "owner@weddingsthyzfoto.com";
      await api.post("/reputation/digest/dispatch", { target_email: target });
      toast.success(`End-of-day reputation digest dispatched to ${target}!`);
      setDigestModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to dispatch daily digest");
    } finally {
      setDigestSending(false);
    }
  };

  const handleSaveSettings = () => {
    toast.success("Reputation triggers & negative gating settings saved!");
  };

  const googleConnected = !!integrations?.google?.connected;
  const yelpConnected = !!integrations?.yelp?.connected;

  const reviews = (reputationData?.reviews && reputationData.reviews.length > 0) ? reputationData.reviews : DEFAULT_REVIEWS;
  const avgRating = reputationData?.average_rating || 4.9;
  const totalReviews = reputationData?.total_reviews || 248;

  const reviewLink = "https://deck.weddingsthyzfoto.com/review/colibri-media";

  const getWidgetCode = () => {
    if (selectedWidgetType === "badge") {
      return `<script src="https://deck.weddingsthyzfoto.com/embed/review-badge.js" data-business="colibri-media" data-theme="${widgetTheme}" data-position="bottom-right"></script>`;
    } else if (selectedWidgetType === "carousel") {
      return `<div id="colibri-reviews-carousel" data-business="colibri-media" data-theme="${widgetTheme}"></div>\n<script src="https://deck.weddingsthyzfoto.com/embed/reviews-carousel.js"></script>`;
    } else {
      return `<div id="colibri-rating-ribbon" data-business="colibri-media" data-theme="${widgetTheme}"></div>\n<script src="https://deck.weddingsthyzfoto.com/embed/reviews-ribbon.js"></script>`;
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-8 max-w-[1600px] mx-auto w-full" data-testid="reputation-page">
      {/* ══════════════════════════════════════════════════════════════
          1. TAB: OVERVIEW
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Subtabs + Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-6 text-sm font-bold">
              {[
                { id: "overview", label: "Overview" },
                { id: "stats", label: "My Stats" },
                { id: "competitors", label: "Competitor Analysis" }
              ].map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => setSecondaryTab(sub.id)}
                  className={`pb-2 transition-all cursor-pointer text-sm font-bold ${
                    secondaryTab === sub.id
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-200"
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="text-sm font-semibold gap-1.5 h-9" onClick={() => setReviewLinkOpen(true)}>
                <Share2 className="w-4 h-4 text-slate-500" />
                <span>Review Link</span>
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm gap-1.5 h-9 shadow-xs" onClick={() => setSendOpen(true)}>
                <Send className="w-4 h-4" />
                <span>Send Review Request</span>
              </Button>
            </div>
          </div>

          {/* ── Subview A: OVERVIEW ── */}
          {secondaryTab === "overview" && (
            <div className="space-y-6">
              {/* Filter Bar */}
              <Card className="border p-3.5 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <select
                      value={sourceFilter}
                      onChange={(e) => setSourceFilter(e.target.value)}
                      className="h-9 rounded-md border px-3 text-xs bg-transparent"
                      style={{ borderColor: "var(--border)", color: "var(--text)" }}
                    >
                      <option value="All">All Review Sources</option>
                      <option value="Google">Google Business Profile</option>
                      <option value="Yelp">Yelp for Business</option>
                      <option value="Direct">Direct Reviews</option>
                    </select>
                    <Button variant="outline" size="sm" className="h-9 gap-1.5 text-xs">
                      <CheckSquare className="w-3.5 h-3.5" /> All Sections
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input type="text" placeholder="DD / MM / YYYY" className="h-9 w-32 text-xs" defaultValue="01/05/2026" />
                    <span className="text-xs text-slate-400">to</span>
                    <Input type="text" placeholder="DD / MM / YYYY" className="h-9 w-32 text-xs" defaultValue="30/11/2026" />
                    <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => loadData()}>
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Onboarding Checklist Card */}
              <Card className="p-6 border relative overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                  <div className="space-y-2 lg:w-1/3">
                    <div className="text-3xl">👋</div>
                    <h3 className="text-xl font-bold" style={{ color: "var(--text)" }}>Hi {user?.name?.split(" ")[0] || "Studio Owner"}</h3>
                    <p className="text-xs" style={{ color: "var(--text-soft)" }}>
                      Finish all steps to maximize your local Google &amp; Yelp review flow.
                    </p>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                      <ShieldCheck className="w-3.5 h-3.5" /> 5 of 6 Steps Completed
                    </div>
                  </div>

                  <div className="lg:w-2/3 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div
                        onClick={() => setGoogleOpen(true)}
                        className="p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors bg-slate-50 dark:bg-slate-900/60"
                        style={{ borderColor: "var(--border)" }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border border-emerald-500 bg-emerald-500 text-white flex items-center justify-center">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>Connect Google Business Profile</span>
                        </div>
                        <span className="text-xs text-blue-600 font-semibold">Active ✓</span>
                      </div>

                      <div
                        onClick={() => setReviewLinkOpen(true)}
                        className="p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors bg-slate-50 dark:bg-slate-900/60"
                        style={{ borderColor: "var(--border)" }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border border-emerald-500 bg-emerald-500 text-white flex items-center justify-center">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>Setup Review Shortlink</span>
                        </div>
                        <span className="text-xs text-blue-600 font-semibold">View →</span>
                      </div>

                      <div
                        onClick={() => setActiveTab("settings")}
                        className="p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors bg-slate-50 dark:bg-slate-900/60"
                        style={{ borderColor: "var(--border)" }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border border-emerald-500 bg-emerald-500 text-white flex items-center justify-center">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>Negative Feedback Gating</span>
                        </div>
                        <span className="text-xs text-blue-600 font-semibold">Configured ✓</span>
                      </div>

                      <div
                        onClick={() => setActiveTab("widgets")}
                        className="p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors bg-slate-50 dark:bg-slate-900/60"
                        style={{ borderColor: "var(--border)" }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border border-emerald-500 bg-emerald-500 text-white flex items-center justify-center">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>Create Website Widget</span>
                        </div>
                        <span className="text-xs text-blue-600 font-semibold">Open →</span>
                      </div>

                      <div
                        onClick={() => setSendOpen(true)}
                        className="p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors bg-slate-50 dark:bg-slate-900/60"
                        style={{ borderColor: "var(--border)" }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border border-emerald-500 bg-emerald-500 text-white flex items-center justify-center">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>Send Review Request</span>
                        </div>
                        <span className="text-xs text-blue-600 font-semibold">Send →</span>
                      </div>

                      <div
                        onClick={() => setYelpOpen(true)}
                        className="p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:border-blue-500 transition-colors bg-slate-50 dark:bg-slate-900/60"
                        style={{ borderColor: "var(--border)" }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border border-slate-300" />
                          <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>Connect Yelp Profile</span>
                        </div>
                        <span className="text-xs text-blue-600 font-semibold">Connect →</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Review Trend Chart */}
              <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div>
                  <h3 className="text-lg font-bold" style={{ color: "var(--text)" }}>Review Acquisition Velocity &amp; Ratings Trend</h3>
                  <p className="text-xs" style={{ color: "var(--text-soft)" }}>
                    Track monthly customer review generation volume and average star rating over time.
                  </p>
                </div>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={TREND_DATA}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip contentStyle={{ background: "var(--surface)", borderColor: "var(--border)", borderRadius: "8px" }} />
                      <Bar dataKey="reviews" fill="#2563EB" radius={[4, 4, 0, 0]} name="New Verified Reviews" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Metric KPI Row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Card className="p-6 border text-center space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Average Customer Rating</div>
                  <div className="text-5xl font-black text-amber-500">{avgRating.toFixed(1)}</div>
                  <div className="flex items-center justify-center gap-1 my-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div className="text-xs text-slate-500">Based on 248 total customer ratings</div>
                </Card>

                <Card className="p-6 border text-center space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Verified Reviews</div>
                  <div className="text-5xl font-black text-blue-600">{totalReviews}</div>
                  <div className="text-xs text-emerald-600 font-semibold">+24 reviews this month</div>
                  <div className="text-xs text-slate-500">Across Google, Yelp &amp; Direct forms</div>
                </Card>

                <Card className="p-6 border text-center space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Sync Gateways</div>
                  <div className="text-5xl font-black text-emerald-600">5</div>
                  <div className="text-xs text-slate-500">Google, Yelp, Apple Maps, Bing, FB</div>
                </Card>
              </div>
            </div>
          )}

          {/* ── Subview B: MY STATS ── */}
          {secondaryTab === "stats" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
                  Detailed Reputation &amp; Sentiment Analytics
                </h3>
                <p className="text-xs text-slate-500">
                  Granular distribution of star ratings, Net Promoter Score, and sentiment analysis.
                </p>
              </div>

              {/* Star Distribution Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-6 border rounded-xl space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-center space-y-1">
                    <div className="text-5xl font-black text-amber-500">4.9</div>
                    <div className="flex items-center justify-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <div className="text-xs text-slate-500">Overall Rating from 248 Reviews</div>
                  </div>

                  <div className="space-y-2.5 pt-2">
                    {[
                      { star: 5, count: 218, pct: 88 },
                      { star: 4, count: 22, pct: 9 },
                      { star: 3, count: 5, pct: 2 },
                      { star: 2, count: 2, pct: 1 },
                      { star: 1, count: 1, pct: 0 },
                    ].map((row) => (
                      <div key={row.star} className="flex items-center gap-3 text-xs">
                        <span className="w-7 font-bold text-slate-700 dark:text-slate-300">{row.star} ★</span>
                        <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full" style={{ width: `${row.pct}%` }} />
                        </div>
                        <span className="w-12 text-right font-mono text-slate-500">{row.count} ({row.pct}%)</span>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Net Promoter Score */}
                <Card className="p-6 border rounded-xl flex flex-col justify-between" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Net Promoter Score (NPS)</div>
                    <div className="text-5xl font-black text-emerald-600 mt-2">+78</div>
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs mt-2">
                      World Class Benchmark
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed mt-4">
                    Scores above +70 represent industry-leading brand loyalty and referrability. 84% of clients identify as active brand promoters.
                  </p>
                  <div className="pt-4 border-t flex justify-between text-xs text-slate-500" style={{ borderColor: "var(--border)" }}>
                    <span>Promoters: <strong>84%</strong></span>
                    <span>Passives: <strong>12%</strong></span>
                    <span>Detractors: <strong>4%</strong></span>
                  </div>
                </Card>

                {/* Sentiment Breakdown */}
                <Card className="p-6 border rounded-xl flex flex-col justify-between" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">AI Sentiment Analysis</div>
                    <div className="text-5xl font-black text-blue-600 mt-2">94%</div>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-xs mt-2">
                      Strongly Positive
                    </Badge>
                  </div>
                  <div className="space-y-2 mt-4 text-xs">
                    <div className="flex justify-between">
                      <span className="text-emerald-600 font-semibold">Positive Sentiment:</span>
                      <span className="font-mono font-bold">94%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-amber-600 font-semibold">Neutral / Inquiries:</span>
                      <span className="font-mono font-bold">4%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600 font-semibold">Critical / Gated:</span>
                      <span className="font-mono font-bold">2%</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t text-xs text-slate-500" style={{ borderColor: "var(--border)" }}>
                    Avg Response Time: <strong className="text-slate-800 dark:text-slate-200">1.4 hours</strong> (Industry: 24h)
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* ── Subview C: COMPETITOR ANALYSIS ── */}
          {secondaryTab === "competitors" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
                    Local Market Competitor Benchmark Intelligence
                  </h3>
                  <p className="text-xs text-slate-500">
                    Compare Colibri Media against top photography &amp; media agencies in Northern California.
                  </p>
                </div>

                <Button
                  size="sm"
                  onClick={() => setAddCompetitorOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs gap-1.5 h-9"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>+ Track Competitor</span>
                </Button>
              </div>

              {/* Competitors Table */}
              <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                        <th className="py-3.5 px-4">Agency / Competitor</th>
                        <th className="py-3.5 px-4">Avg Rating</th>
                        <th className="py-3.5 px-4">Total Reviews</th>
                        <th className="py-3.5 px-4">Response Time</th>
                        <th className="py-3.5 px-4">Positive Sentiment</th>
                        <th className="py-3.5 px-4">Local Map Rank</th>
                        <th className="py-3.5 px-4 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                      {competitors.map((c) => (
                        <tr
                          key={c.name}
                          className={`transition-colors ${
                            c.isSelf
                              ? "bg-blue-50/60 dark:bg-blue-950/20 font-bold"
                              : "hover:bg-slate-50 dark:hover:bg-slate-800/40"
                          }`}
                        >
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2">
                              {c.isSelf && <Award className="w-4 h-4 text-blue-600 shrink-0" />}
                              <span style={{ color: "var(--text)" }}>{c.name}</span>
                            </div>
                          </td>
                          <td className="py-3.5 px-4 text-amber-500 font-bold">
                            ★ {c.rating.toFixed(1)}
                          </td>
                          <td className="py-3.5 px-4 font-mono text-xs">{c.reviews}</td>
                          <td className="py-3.5 px-4 text-xs text-slate-500">{c.responseTime}</td>
                          <td className="py-3.5 px-4 text-xs font-semibold text-emerald-600">{c.sentiment}</td>
                          <td className="py-3.5 px-4">
                            <Badge variant="outline" className={`text-xs ${c.isSelf ? "bg-emerald-50 text-emerald-700 border-emerald-300" : ""}`}>
                              {c.rank}
                            </Badge>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            {c.isSelf ? (
                              <Badge className="bg-blue-600 text-white text-xs">Your Studio</Badge>
                            ) : (
                              <span className="text-xs text-slate-400">Tracking</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Competitive Insights Card */}
              <Card className="p-6 border rounded-xl space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex items-center gap-2 font-bold text-sm text-blue-600">
                  <Sparkles className="w-4 h-4" />
                  <span>Colibri Media Competitive Edge</span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  Colibri Media leads the local market pack in review velocity (+24 reviews/mo vs competitor avg +6) and maintains a <strong>1.4-hour owner response rate</strong> (4.2x faster than Pacific Coast Photo). Your Google Business Profile optimization score of 96/100 ensures primary map placement for "commercial photographer bay area".
                </p>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          2. TAB: REQUESTS (Review Request Log)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "requests" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Review Requests</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Track review invitation messages sent via SMS (Twilio) and transactional email.
              </p>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm gap-1.5 h-9 shadow-xs" onClick={() => setSendOpen(true)}>
              <Send className="w-4 h-4" /> Send Review Request
            </Button>
          </div>

          <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                    <th className="py-3.5 px-4">Request ID</th>
                    <th className="py-3.5 px-4">Customer Name</th>
                    <th className="py-3.5 px-4">Contact</th>
                    <th className="py-3.5 px-4">Channel</th>
                    <th className="py-3.5 px-4">Date Sent</th>
                    <th className="py-3.5 px-4">Delivery Status</th>
                    <th className="py-3.5 px-4">Review Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {requests.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-mono text-xs text-slate-500">{r.id}</td>
                      <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>{r.name}</td>
                      <td className="py-3.5 px-4 font-mono text-xs text-slate-500">{r.contact}</td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{r.channel}</Badge>
                      </td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{r.date}</td>
                      <td className="py-3.5 px-4">
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                          <CheckCircle2 className="w-3.5 h-3.5" /> {r.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <Badge className={`text-xs ${r.outcome.includes("5★") ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                          {r.outcome}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          3. TAB: REVIEWS (Live Customer Reviews)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "reviews" && (() => {
        const flaggedList = reviews.filter(r => r.flagged || r.status === "flagged" || r.sentiment === "urgent_action");
        const cleanScore = 100;

        const filteredReviews = reviews.filter((r) => {
          if (reviewCategoryFilter === "attention") {
            if (!r.flagged && r.status !== "flagged" && r.sentiment !== "urgent_action") return false;
          } else if (reviewCategoryFilter === "5star") {
            if ((r.rating || 5) < 5) return false;
          } else if (reviewCategoryFilter === "google") {
            if (!r.source?.toLowerCase().includes("google")) return false;
          } else if (reviewCategoryFilter === "yelp") {
            if (!r.source?.toLowerCase().includes("yelp")) return false;
          }

          if (reviewSearch) {
            const q = reviewSearch.toLowerCase();
            return (
              r.author?.toLowerCase().includes(q) ||
              r.text?.toLowerCase().includes(q) ||
              r.source?.toLowerCase().includes(q)
            );
          }
          return true;
        });

        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Customer Reviews &amp; Clean Sheet Guardian</h2>
                <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                  Live multi-channel feedback across Google, Yelp, and Direct Web.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search reviews or topics…"
                    value={reviewSearch}
                    onChange={(e) => setReviewSearch(e.target.value)}
                    className="h-9 w-56 pl-8 text-xs bg-white dark:bg-slate-900"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={() => loadData()}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync Reviews
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs"
                  onClick={() => {
                    setDigestEmail(user?.email || "owner@weddingsthyzfoto.com");
                    setDigestModalOpen(true);
                  }}
                >
                  <Mail className="w-3.5 h-3.5" /> End-of-Day Digest
                </Button>
              </div>
            </div>

            {/* Clean Sheet Score Banner */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-5 border flex flex-col justify-between rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase text-slate-400 tracking-wider">Clean Sheet Health</span>
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1" /> 100% Spotless
                  </Badge>
                </div>
                <div className="mt-2">
                  <div className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">100%</div>
                  <p className="text-xs text-slate-500 mt-1">Zero unresolved customer disputes across directories.</p>
                </div>
              </Card>

              <Card className="p-5 border flex flex-col justify-between rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase text-slate-400 tracking-wider">Attention &amp; Dispute Queue</span>
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    0 Cases
                  </Badge>
                </div>
                <div className="mt-2">
                  <div className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">0</div>
                  <p className="text-xs text-slate-500 mt-1">All negative or constructive ratings gated to private resolution.</p>
                </div>
              </Card>

              <Card className="p-5 border flex flex-col justify-between rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase text-slate-400 tracking-wider">Average Star Rating</span>
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                    ★ 4.9 Rating
                  </Badge>
                </div>
                <div className="mt-2">
                  <div className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">4.9 / 5.0</div>
                  <p className="text-xs text-slate-500 mt-1">Top-rated photography &amp; production studio in the region.</p>
                </div>
              </Card>
            </div>

            {/* Filter Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs font-semibold">
              {[
                { id: "all", label: "All Reviews" },
                { id: "5star", label: "5-Star Ratings" },
                { id: "google", label: "Google Business Profile" },
                { id: "yelp", label: "Yelp" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setReviewCategoryFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${
                    reviewCategoryFilter === tab.id
                      ? "bg-blue-600 text-white border-blue-600 font-bold"
                      : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Review Cards Stream */}
            <div className="space-y-4">
              {filteredReviews.map((r) => (
                <Card key={r.id} className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-700 dark:text-slate-200 text-sm shrink-0">
                        {r.author?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-slate-900 dark:text-white">{r.author}</span>
                          <span className="text-xs text-slate-400 font-mono">#{r.id}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-0.5">
                            {[...Array(r.rating || 5)].map((_, idx) => (
                              <Star key={idx} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                            ))}
                          </div>
                          <span className="text-xs text-slate-400">· {r.date}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs font-semibold">
                        {r.source}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-8 gap-1"
                        onClick={() => {
                          setReviewToReply(r);
                          setReplyDraft(r.reply || "");
                          setReplyModalOpen(true);
                        }}
                      >
                        <Reply className="w-3.5 h-3.5" />
                        <span>{r.replied ? "Edit Reply" : "Reply"}</span>
                      </Button>
                    </div>
                  </div>

                  <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-sans">
                    "{r.text}"
                  </p>

                  {r.replied && r.reply && (
                    <div className="p-3.5 rounded-lg bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 text-xs space-y-1">
                      <div className="flex items-center gap-1.5 font-bold text-blue-700 dark:text-blue-300">
                        <CornerDownRight className="w-3.5 h-3.5" />
                        <span>Owner Response ({r.replied_at || "Published"}):</span>
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 pl-5">{r.reply}</p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════════
          4. TAB: VIDEO TESTIMONIALS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "testimonials" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Video Testimonials</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Collect recorded video reviews from clients on their mobile phones with zero app downloads.
              </p>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm gap-1.5 h-9 shadow-xs" onClick={() => setRequestVideoOpen(true)}>
              <Plus className="w-4 h-4" /> Request Video Testimonial
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {videos.map((v) => (
              <Card key={v.id} className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div
                  className="h-48 bg-slate-900 relative flex items-center justify-center cursor-pointer group"
                  onClick={() => {
                    setActiveVideo(v);
                    setVideoModalOpen(true);
                  }}
                >
                  <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-white fill-white ml-1" />
                  </div>
                  <div className="absolute top-3 left-3 px-2 py-0.5 rounded text-xs font-bold bg-black/60 text-white font-mono">{v.duration}</div>
                  <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded text-xs font-bold bg-emerald-500 text-white">Click to Watch</div>
                </div>
                <div className="p-5 space-y-2.5">
                  <div className="font-bold text-sm" style={{ color: "var(--text)" }}>{v.client} - {v.company}</div>
                  <p className="text-xs text-slate-500 leading-relaxed">"{v.quote}"</p>
                  <div className="flex items-center justify-between pt-3 border-t text-xs" style={{ borderColor: "var(--border)" }}>
                    <span className="text-emerald-600 font-semibold">{v.status}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-blue-600 font-semibold"
                      onClick={() => {
                        navigator.clipboard.writeText(`<video src="${v.videoUrl}" controls></video>`);
                        toast.success("Embed video code copied!");
                      }}
                    >
                      Copy Embed
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          5. TAB: WIDGETS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "widgets" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Website Review Widgets</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Embed live Google and Yelp star ratings on your website with 1 line of HTML.
              </p>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm gap-1.5 h-9" onClick={() => {
              navigator.clipboard.writeText(getWidgetCode());
              setWidgetCopied(true);
              toast.success("Embed script copied to clipboard!");
              setTimeout(() => setWidgetCopied(false), 2000);
            }}>
              {widgetCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span>{widgetCopied ? "Copied" : "Copy Embed Script"}</span>
            </Button>
          </div>

          {/* Widget Format Selector */}
          <div className="flex items-center gap-3">
            {[
              { id: "badge", label: "Floating Star Badge" },
              { id: "carousel", label: "Wall of Love (Carousel)" },
              { id: "ribbon", label: "Minimalist Star Ribbon" }
            ].map(type => (
              <button
                key={type.id}
                onClick={() => setSelectedWidgetType(type.id)}
                className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                  selectedWidgetType === type.id
                    ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800"
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Live Widget Preview</h3>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span>Theme:</span>
                  <button
                    onClick={() => setWidgetTheme(widgetTheme === "light" ? "dark" : "light")}
                    className="font-bold text-blue-600 underline cursor-pointer"
                  >
                    {widgetTheme === "light" ? "Light" : "Dark"}
                  </button>
                </div>
              </div>

              {selectedWidgetType === "badge" && (
                <div className={`p-6 rounded-2xl border flex items-center gap-4 max-w-sm shadow-lg ${widgetTheme === "dark" ? "bg-slate-950 text-white border-slate-800" : "bg-white text-slate-900 border-slate-200"}`}>
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-black text-xl">5.0</div>
                  <div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <div className="text-xs font-semibold mt-1">Based on 248+ Google &amp; Yelp Reviews</div>
                  </div>
                </div>
              )}

              {selectedWidgetType === "carousel" && (
                <div className={`p-4 rounded-2xl border space-y-3 ${widgetTheme === "dark" ? "bg-slate-950 text-white border-slate-800" : "bg-white text-slate-900 border-slate-200"}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold">Colibri Media Verified Testimonials</span>
                    <span className="text-amber-400 font-bold">★★★★★ 4.9</span>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs italic">
                    "Colibri Media elevated our photoshoot workflows and delivered within 48 hours. Five stars all around!"
                  </div>
                  <div className="text-[11px] text-slate-400 text-right">— Elena R., Grand Haven Hotel</div>
                </div>
              )}

              {selectedWidgetType === "ribbon" && (
                <div className={`p-3 rounded-xl border flex items-center justify-between text-xs font-bold ${widgetTheme === "dark" ? "bg-slate-950 text-white border-slate-800" : "bg-white text-slate-900 border-slate-200"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-amber-400">★★★★★</span>
                    <span>4.9 Star Rating</span>
                  </div>
                  <span className="text-blue-600 font-semibold">248 Verified Reviews on Google</span>
                </div>
              )}
            </Card>

            <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Embed HTML Code</h3>
              <div className="p-3.5 rounded-lg bg-slate-900 text-emerald-400 font-mono text-xs overflow-x-auto whitespace-pre">
                {getWidgetCode()}
              </div>
              <p className="text-xs text-slate-400">
                Paste this script before the &lt;/body&gt; closing tag on your website or custom landing page.
              </p>
            </Card>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          6. TAB: LISTINGS (Local Directories)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "listings" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Local Business Listings</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Ensure consistent Name, Address, and Phone (NAP) across major maps and search engines.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={auditingListings}
              onClick={handleRunListingAudit}
              className="text-xs font-semibold gap-1.5 h-9"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${auditingListings ? "animate-spin" : ""}`} />
              <span>{auditingListings ? "Auditing Directories…" : "Run Listing Audit"}</span>
            </Button>
          </div>

          <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                    <th className="py-3.5 px-4">Directory / Platform</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4">NAP Consistency</th>
                    <th className="py-3.5 px-4">Last Sync</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {listings.map((l) => (
                    <tr key={l.platform} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>{l.platform}</td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className={`text-xs ${l.status === "Connected" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
                          {l.status}
                        </Badge>
                      </td>
                      <td className="py-3.5 px-4 font-mono font-bold text-emerald-600">{l.sync}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{l.lastSync}</td>
                      <td className="py-3.5 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-blue-600 font-semibold"
                          onClick={() => handleSyncListing(l.platform)}
                        >
                          Sync Now
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          7. TAB: GBP OPTIMIZATION
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "gbp" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Google Business Profile Optimization</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Audit profile completeness, local SEO ranking keywords, and weekly view analytics.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => toast.success("Google Business Profile data refreshed with live Google Maps API!")}
              className="text-xs font-semibold gap-1.5 h-9"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh Profile Data
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 border text-center space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold text-slate-500 uppercase">Profile Completeness Score</div>
              <div className="text-5xl font-black text-emerald-600">96%</div>
              <div className="text-xs text-slate-500">Categories, hours &amp; attributes verified</div>
            </Card>

            <Card className="p-6 border text-center space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold text-slate-500 uppercase">Search View Impressions</div>
              <div className="text-5xl font-black text-blue-600">1,840</div>
              <div className="text-xs text-emerald-600 font-semibold">+28% vs previous 30 days</div>
            </Card>

            <Card className="p-6 border text-center space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold text-slate-500 uppercase">Local 3-Pack Rank</div>
              <div className="text-5xl font-black text-purple-600">#1</div>
              <div className="text-xs text-slate-500">Top map position for photography keywords</div>
            </Card>
          </div>

          {/* Top Search Keywords Table */}
          <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="p-4 border-b font-bold text-sm" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
              Top Local Search Queries Driving Profile Views
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                    <th className="py-3 px-4">Search Query / Keyword</th>
                    <th className="py-3 px-4">Monthly Local Volume</th>
                    <th className="py-3 px-4">Colibri Map Rank</th>
                    <th className="py-3 px-4 text-right">Profile Clicks</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {[
                    { query: "commercial photographer near me", volume: "1,200/mo", rank: "#1", clicks: "418" },
                    { query: "wedding photography studio bay area", volume: "850/mo", rank: "#1", clicks: "312" },
                    { query: "corporate headshots san francisco", volume: "620/mo", rank: "#2", clicks: "194" },
                    { query: "product video production studio", volume: "480/mo", rank: "#1", clicks: "165" },
                  ].map((k) => (
                    <tr key={k.query} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="py-3 px-4 font-semibold" style={{ color: "var(--text)" }}>{k.query}</td>
                      <td className="py-3 px-4 text-xs font-mono text-slate-500">{k.volume}</td>
                      <td className="py-3 px-4 text-xs font-bold text-emerald-600">{k.rank}</td>
                      <td className="py-3 px-4 text-xs font-mono text-right font-bold" style={{ color: "var(--text)" }}>{k.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          8. TAB: SETTINGS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "settings" && (
        <div className="space-y-6 max-w-4xl">
          <div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Reputation &amp; Gating Settings</h2>
            <p className="text-sm" style={{ color: "var(--text-soft)" }}>
              Manage review request triggers, AI sentiment filters, and custom feedback redirects.
            </p>
          </div>

          <Card className="p-6 border space-y-5 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase">Automated Review Request Timing</label>
              <select
                value={reputationSettings.timing}
                onChange={(e) => setReputationSettings({ ...reputationSettings, timing: e.target.value })}
                className="w-full h-10 rounded-md border px-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <option value="1">Send 1 hour after photoshoot or job completed</option>
                <option value="24">Send 24 hours after client invoice paid</option>
                <option value="manual">Manual dispatch only</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase">Negative Feedback Gating</label>
              <p className="text-xs text-slate-400">
                Direct ratings under 4 stars to a private resolution form before public posting to safeguard 100% clean sheet.
              </p>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="gatingCheck"
                  checked={reputationSettings.negativeGating}
                  onChange={(e) => setReputationSettings({ ...reputationSettings, negativeGating: e.target.checked })}
                  className="rounded w-4 h-4"
                />
                <label htmlFor="gatingCheck" className="text-sm font-semibold cursor-pointer" style={{ color: "var(--text)" }}>
                  Enable private resolution for &lt; 4 star ratings
                </label>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase">Custom SMS Review Invitation Text</label>
              <Textarea
                rows={3}
                value={reputationSettings.smsTemplate}
                onChange={(e) => setReputationSettings({ ...reputationSettings, smsTemplate: e.target.value })}
                className="text-xs font-mono"
              />
              <span className="text-[11px] text-slate-400">Supported variables: &#123;client_name&#125;, &#123;review_link&#125;</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-500 uppercase">Owner Alert Email for Negative Feedback</label>
                <Input
                  value={reputationSettings.alertEmail}
                  onChange={(e) => setReputationSettings({ ...reputationSettings, alertEmail: e.target.value })}
                  className="text-xs font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-500 uppercase">Urgent Alert SMS Phone</label>
                <Input
                  value={reputationSettings.alertPhone}
                  onChange={(e) => setReputationSettings({ ...reputationSettings, alertPhone: e.target.value })}
                  className="text-xs font-mono"
                />
              </div>
            </div>

            <div className="pt-3 border-t flex justify-end" style={{ borderColor: "var(--border)" }}>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-6" onClick={handleSaveSettings}>
                Save Reputation Settings
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ── Send Review Request Modal ── */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Customer Review Request</DialogTitle>
            <DialogDescription>
              Invite clients to leave a Google or Yelp review with 1 click.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendRequest} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Customer Name</Label>
              <Input
                value={reqForm.name}
                onChange={(e) => setReqForm({ ...reqForm, name: e.target.value })}
                placeholder="e.g. David Miller"
                className="mt-1 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Delivery Channel</Label>
              <div className="flex items-center gap-4 mt-1.5">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="radio"
                    checked={reqForm.channel === "SMS"}
                    onChange={() => setReqForm({ ...reqForm, channel: "SMS" })}
                  />
                  <span>SMS (Twilio)</span>
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="radio"
                    checked={reqForm.channel === "Email"}
                    onChange={() => setReqForm({ ...reqForm, channel: "Email" })}
                  />
                  <span>Email</span>
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">
                {reqForm.channel === "SMS" ? "Phone Number" : "Email Address"}
              </Label>
              <Input
                value={reqForm.contact}
                onChange={(e) => setReqForm({ ...reqForm, contact: e.target.value })}
                placeholder={reqForm.channel === "SMS" ? "+1 (555) 000-0000" : "client@domain.com"}
                className="mt-1 text-sm font-mono"
                required
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setSendOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm">
                {sending ? "Sending…" : "Send Request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Request Video Testimonial Modal ── */}
      <Dialog open={requestVideoOpen} onOpenChange={setRequestVideoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Video Testimonial</DialogTitle>
            <DialogDescription>
              Sends a mobile video recording link to the client with your custom question prompt.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendVideoRequest} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Client Name</Label>
              <Input
                value={videoReqForm.name}
                onChange={(e) => setVideoReqForm({ ...videoReqForm, name: e.target.value })}
                placeholder="e.g. Elena Rostova"
                className="mt-1 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Client Phone / Email</Label>
              <Input
                value={videoReqForm.contact}
                onChange={(e) => setVideoReqForm({ ...videoReqForm, contact: e.target.value })}
                placeholder="+1 (415) 882-9102"
                className="mt-1 text-sm font-mono"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Video Prompt Question</Label>
              <Input
                value={videoReqForm.prompt}
                onChange={(e) => setVideoReqForm({ ...videoReqForm, prompt: e.target.value })}
                placeholder="e.g. How was your experience working with Colibri Media?"
                className="mt-1 text-sm"
                required
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setRequestVideoOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm">
                Dispatch Video Invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Video Player Modal ── */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{activeVideo?.client} - {activeVideo?.company}</DialogTitle>
            <DialogDescription>Recorded Client Video Testimonial</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="w-full h-64 bg-slate-900 rounded-xl overflow-hidden flex items-center justify-center relative">
              {activeVideo?.videoUrl ? (
                <video src={activeVideo.videoUrl} controls autoPlay className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-white">
                  <Play className="w-12 h-12 mx-auto mb-2 opacity-70" />
                  <span className="text-xs">Playing preview</span>
                </div>
              )}
            </div>
            <div className="p-3.5 bg-slate-50 dark:bg-slate-900 rounded-xl border text-xs space-y-1">
              <span className="font-bold block text-slate-700 dark:text-slate-300">Client Quote Transcript:</span>
              <p className="text-slate-600 dark:text-slate-400 italic leading-relaxed">"{activeVideo?.quote}"</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm"
              onClick={() => {
                toast.success("Approved status confirmed for landing pages!");
                setVideoModalOpen(false);
              }}
            >
              Approved for Website &amp; Portfolio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Competitor Modal ── */}
      <Dialog open={addCompetitorOpen} onOpenChange={setAddCompetitorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Benchmark New Local Competitor</DialogTitle>
            <DialogDescription>
              Track Google Maps &amp; Yelp review movements for a rival studio.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCompetitor} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Competitor Business Name</Label>
              <Input
                value={newCompetitorForm.name}
                onChange={(e) => setNewCompetitorForm({ ...newCompetitorForm, name: e.target.value })}
                placeholder="e.g. Apex Visuals & Cinema"
                className="mt-1 text-sm"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Estimated Rating</Label>
                <Input
                  value={newCompetitorForm.rating}
                  onChange={(e) => setNewCompetitorForm({ ...newCompetitorForm, rating: e.target.value })}
                  placeholder="4.6"
                  className="mt-1 text-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Review Count</Label>
                <Input
                  value={newCompetitorForm.reviews}
                  onChange={(e) => setNewCompetitorForm({ ...newCompetitorForm, reviews: e.target.value })}
                  placeholder="120"
                  className="mt-1 text-sm font-mono"
                />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setAddCompetitorOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm">
                Add Competitor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Review Link Modal ── */}
      <Dialog open={reviewLinkOpen} onOpenChange={setReviewLinkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Customer Review Gateway</DialogTitle>
            <DialogDescription>
              Direct URL for clients with 5-star public gating &amp; private resolution routing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Live Shortlink</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input readOnly value={reviewLink} className="font-mono text-xs bg-slate-50 dark:bg-slate-900" />
                <Button size="sm" variant="outline" className="text-xs font-bold shrink-0" onClick={() => {
                  navigator.clipboard.writeText(reviewLink);
                  toast.success("Review shortlink copied!");
                }}>
                  Copy
                </Button>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 text-xs space-y-2">
              <span className="font-bold text-blue-900 dark:text-blue-200 block">How This Gateway Protects Your Rating:</span>
              <ul className="list-disc list-inside space-y-1 text-blue-800 dark:text-blue-300">
                <li><strong>4–5 Stars:</strong> Client is directed straight to Google &amp; Yelp to post publicly.</li>
                <li><strong>1–3 Stars:</strong> Client feedback routes privately to executive leadership.</li>
              </ul>
            </div>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm gap-2"
              onClick={() => window.open(reviewLink, "_blank")}
            >
              <ExternalLink className="w-4 h-4" /> Open Public Review Page
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Google Connect Modal */}
      <Dialog open={googleOpen} onOpenChange={setGoogleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-red-500" />
              <span>Connect Google Business Profile</span>
            </DialogTitle>
            <DialogDescription>
              Sync reviews, ratings, and automate responses directly via Google Places API.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveGoogle} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Google Place ID</Label>
              <Input
                value={googleForm.place_id}
                onChange={(e) => setGoogleForm({ ...googleForm, place_id: e.target.value })}
                placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
                className="mt-1 font-mono text-xs"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Google Maps API Key</Label>
              <Input
                type="password"
                value={googleForm.api_key}
                onChange={(e) => setGoogleForm({ ...googleForm, api_key: e.target.value })}
                placeholder="AIzaSyD-..."
                className="mt-1 font-mono text-xs"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setGoogleOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Save &amp; Sync Reviews
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Yelp Connect Modal */}
      <Dialog open={yelpOpen} onOpenChange={setYelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-red-600" />
              <span>Connect Yelp for Business</span>
            </DialogTitle>
            <DialogDescription>
              Streamline Yelp review tracking and sync incoming customer feedback.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveYelp} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Yelp Business ID or Alias</Label>
              <Input
                value={yelpForm.business_id}
                onChange={(e) => setYelpForm({ ...yelpForm, business_id: e.target.value })}
                placeholder="colibri-media-san-francisco"
                className="mt-1 font-mono text-xs"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setYelpOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Save &amp; Sync Yelp
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reply Modal */}
      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Reply to {reviewToReply?.author}</DialogTitle>
            <DialogDescription>
              Your official response will be published to {reviewToReply?.source || "the directory"}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePublishReply} className="space-y-4 py-2">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold uppercase text-slate-500">Owner Response</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={generatingAi}
                  onClick={() => handleGenerateSmartReply(reviewToReply)}
                  className="h-7 text-xs text-blue-600 font-semibold gap-1"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{generatingAi ? "Generating AI Response…" : "Draft with AI"}</span>
                </Button>
              </div>
              <Textarea
                rows={5}
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                placeholder="Type your official owner response here..."
                className="text-xs font-sans leading-relaxed"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setReplyModalOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={replying} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                {replying ? "Publishing…" : "Publish Official Reply"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* End-of-Day Review Digest Modal */}
      <Dialog open={digestModalOpen} onOpenChange={setDigestModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <span>End-of-Day Reputation Digest Agent</span>
            </DialogTitle>
            <DialogDescription>
              Dispatches an automated executive summary of customer reviews, sentiment trends, and flagged action items to the owner's inbox.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDispatchDigest} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Recipient Email (Owner / GM)</Label>
              <Input
                type="email"
                value={digestEmail}
                onChange={(e) => setDigestEmail(e.target.value)}
                placeholder="owner@weddingsthyzfoto.com"
                className="mt-1.5 text-xs"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDigestModalOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={digestSending} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                {digestSending ? "Sending Email…" : "Send Daily Digest Email Now"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
